// The rest of the clinic portal: Notice Board, Case Assessment, Book a Scan, Clinical Preferences,
// Clinic Profile. Payment/banking screens are deliberately not here.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatDateTime, type WfClinicPrefs, type WfClinicProfile, type WfNotice, type WfScanBooking, type WfScanSlot } from '@alignova/shared';
import { useLiveEvent } from '../../lib/useLive.ts';
import { portalApi } from './portalApi.ts';
import { NotificationSettings } from '../../ui/NotificationSettings.tsx';

const input = 'w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-[15px] text-ink focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/25';
const lbl = 'mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted md:mb-0 md:pt-3';
const row = 'block md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4';
const card = 'rounded-xl border border-line bg-surface p-5 shadow-level-1';

function Msg({ ok, children }: { ok?: boolean; children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className={`rounded-lg px-4 py-3 text-sm ${ok ? 'border border-status-progress-fg/30 bg-status-progress-bg text-status-progress-fg' : 'border border-status-urgent-fg/30 bg-status-urgent-bg text-status-urgent-fg'}`}>{children}</div>
  );
}

function Title({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <header>
      <h1 className="text-xl font-bold text-ink">{children}</h1>
      {sub && <p className="text-sm text-muted">{sub}</p>}
    </header>
  );
}

// ---------------------------------------------------------------------------------------------

export function PortalNotices() {
  const [items, setItems] = useState<WfNotice[] | null>(null);
  const load = useCallback(() => {
    portalApi.notices().then(setItems).catch(() => setItems([]));
  }, []);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);
  return (
    <div className="space-y-4">
      <Title sub="Announcements from the lab">Notice board</Title>
      {!items && <p className="text-muted">Loading…</p>}
      {items?.length === 0 && <div className="rounded-xl border border-dashed border-line px-6 py-10 text-center text-sm text-muted">No notices right now.</div>}
      <ul className="space-y-3">
        {items?.map((n) => (
          <li key={n.id} className={`${card} ${n.pinned ? 'border-accent/50' : ''}`}>
            <div className="flex items-center gap-2">
              {n.pinned && <span className="rounded bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent">Pinned</span>}
              <h2 className="font-semibold text-ink">{n.title}</h2>
              <span className="ml-auto text-xs text-muted">{formatDate(n.createdAt)}</span>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm text-ink">{n.body}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

export function PortalAssessment() {
  const nav = useNavigate();
  const [f, setF] = useState({ patientFirstName: '', patientLastName: '', chiefComplaint: '', phone: '', notes: '' });
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const c = await portalApi.submitAssessment({
        patientFirstName: f.patientFirstName,
        patientLastName: f.patientLastName || null,
        chiefComplaint: f.chiefComplaint,
        phone: f.phone || null,
        notes: f.notes || null,
      });
      const { uploadPortalRecord } = await import('./portalApi.ts');
      for (const file of files) await uploadPortalRecord(c.id, file).catch(() => undefined);
      nav(`/portal/cases/${c.id}?submitted=1`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Title sub="Send a complaint and photos - the lab will assess and advise before you commit to a full case">Case assessment</Title>
      <Msg>{err}</Msg>
      <div className={`${card} space-y-3`}>
        <label className={row}>
          <span className={lbl}>Patient first name *</span>
          <input className={input} autoFocus value={f.patientFirstName} onChange={(e) => set('patientFirstName', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Patient last name</span>
          <input className={input} value={f.patientLastName} onChange={(e) => set('patientLastName', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Contact</span>
          <input className={input} value={f.phone} onChange={(e) => set('phone', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Chief complaint *</span>
          <textarea className={input} rows={3} value={f.chiefComplaint} onChange={(e) => set('chiefComplaint', e.target.value)} placeholder="What is the patient concerned about?" />
        </label>
        <label className={row}>
          <span className={lbl}>Notes</span>
          <textarea className={input} rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Photos / X-rays</span>
          <input type="file" multiple className="text-sm" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
        </label>
        {files.length > 0 && <p className="text-xs text-muted md:pl-[11.75rem]">{files.length} file(s) attached</p>}
      </div>
      <div className="flex justify-end">
        <button
          disabled={busy || !f.patientFirstName.trim() || f.chiefComplaint.trim().length < 3}
          onClick={() => void submit()}
          className="rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-accent-fg disabled:opacity-40"
        >
          {busy ? 'Sending…' : 'Send for assessment'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

export function PortalBookScan() {
  const [slots, setSlots] = useState<WfScanSlot[] | null>(null);
  const [bookings, setBookings] = useState<WfScanBooking[]>([]);
  const [sel, setSel] = useState('');
  const [f, setF] = useState({ patientName: '', contact: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const load = useCallback(() => {
    portalApi.slots().then(setSlots).catch(() => setSlots([]));
    portalApi.bookings().then(setBookings).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  const book = async () => {
    setBusy(true);
    setErr(null);
    setOk(null);
    try {
      await portalApi.book({ slotId: sel, patientName: f.patientName, contact: f.contact || null, note: f.note || null });
      setOk('Slot booked. You will see it below.');
      setSel('');
      setF({ patientName: '', contact: '', note: '' });
      load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (b: WfScanBooking) => {
    const reason = window.prompt('Reason for cancelling?');
    if (!reason?.trim()) return;
    await portalApi.cancelBooking(b.id, reason.trim()).catch(() => undefined);
    load();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Title sub="Reserve an in-house scanning appointment for your patient">Book a scan</Title>
      <Msg>{err}</Msg>
      {ok && <Msg ok>{ok}</Msg>}
      <section className={card}>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Available slots</h2>
        {!slots && <p className="text-sm text-muted">Loading…</p>}
        {slots?.length === 0 && <p className="text-sm text-muted">No slots are open right now. The lab publishes new slots regularly - or message them on a case.</p>}
        <div className="flex flex-wrap gap-2">
          {slots?.map((s) => (
            <button
              key={s.id}
              onClick={() => setSel(s.id === sel ? '' : s.id)}
              className={`rounded-lg border px-3 py-2 text-left text-sm ${sel === s.id ? 'border-accent bg-accent/10' : 'border-line hover:bg-surface-2'}`}
            >
              <div className="font-medium text-ink">{formatDateTime(s.startsAt)}</div>
              <div className="text-xs text-muted">
                {s.durationMin} min{s.location ? ` · ${s.location}` : ''} · {s.capacity - s.booked} left
              </div>
            </button>
          ))}
        </div>
        {sel && (
          <div className="mt-4 space-y-3 border-t border-line pt-4">
            <label className={row}>
              <span className={lbl}>Patient name *</span>
              <input className={input} autoFocus value={f.patientName} onChange={(e) => setF({ ...f, patientName: e.target.value })} />
            </label>
            <label className={row}>
              <span className={lbl}>Contact</span>
              <input className={input} value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} />
            </label>
            <label className={row}>
              <span className={lbl}>Note</span>
              <textarea className={input} rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            </label>
            <div className="flex justify-end">
              <button disabled={busy || !f.patientName.trim()} onClick={() => void book()} className="rounded-lg bg-accent px-5 py-2 text-sm font-semibold text-accent-fg disabled:opacity-40">
                {busy ? 'Booking…' : 'Confirm booking'}
              </button>
            </div>
          </div>
        )}
      </section>

      <section className={card}>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Your bookings</h2>
        {bookings.length ? (
          <ul className="space-y-2">
            {bookings.map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink">{b.patientName}</div>
                  <div className="text-xs text-muted">
                    {formatDateTime(b.startsAt)}
                    {b.location ? ` · ${b.location}` : ''}
                  </div>
                  {b.cancelReason && <div className="text-xs text-status-urgent-fg">Cancelled: {b.cancelReason}</div>}
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs ${b.status === 'booked' ? 'bg-status-progress-bg text-status-progress-fg' : 'bg-surface-2 text-muted'}`}>{b.status}</span>
                {b.status === 'booked' && (
                  <button onClick={() => void cancel(b)} className="text-xs text-status-urgent-fg hover:underline">
                    Cancel
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">No bookings yet.</p>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

export function PortalPreferences() {
  const [p, setP] = useState<WfClinicPrefs | null>(null);
  const [meta, setMeta] = useState<Array<{ key: string; label: string }>>([]);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    portalApi.preferences().then(setP).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
    portalApi.meta().then((m) => setMeta(m.caseTypes)).catch(() => undefined);
  }, []);
  const set = <K extends keyof WfClinicPrefs>(k: K, v: WfClinicPrefs[K]) => setP((prev) => (prev ? { ...prev, [k]: v } : prev));
  if (err) return <Msg>{err}</Msg>;
  if (!p) return <p className="text-muted">Loading…</p>;
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Title sub="Defaults the lab applies to your cases unless you say otherwise">Clinical preferences</Title>
      {ok && <Msg ok>{ok}</Msg>}
      <NotificationSettings />
      <div className={`${card} space-y-3`}>
        <label className={row}>
          <span className={lbl}>Default case type</span>
          <select className={input} value={p.defaultCaseType ?? ''} onChange={(e) => set('defaultCaseType', e.target.value || null)}>
            <option value="">- none -</option>
            {meta.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
        <label className={row}>
          <span className={lbl}>Default arches</span>
          <select className={input} value={p.defaultArch ?? ''} onChange={(e) => set('defaultArch', e.target.value || null)}>
            <option value="">- none -</option>
            <option value="upper">Upper only</option>
            <option value="lower">Lower only</option>
            <option value="both">Both arches</option>
          </select>
        </label>
        <label className={row}>
          <span className={lbl}>Default priority</span>
          <select className={input} value={p.defaultPriority ?? ''} onChange={(e) => set('defaultPriority', (e.target.value || null) as WfClinicPrefs['defaultPriority'])}>
            <option value="">- none -</option>
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
            <option value="emergency">Emergency</option>
          </select>
        </label>
        <label className={row}>
          <span className={lbl}>IPR preference</span>
          <select className={input} value={p.iprPreference ?? ''} onChange={(e) => set('iprPreference', (e.target.value || null) as WfClinicPrefs['iprPreference'])}>
            <option value="">- none -</option>
            <option value="not_required">Not required</option>
            <option value="required">Required</option>
          </select>
        </label>
        <label className={row}>
          <span className={lbl}>Etching preference</span>
          <select className={input} value={p.etchingPreference ?? ''} onChange={(e) => set('etchingPreference', (e.target.value || null) as WfClinicPrefs['etchingPreference'])}>
            <option value="">- none -</option>
            <option value="not_required">Not required</option>
            <option value="required">Required</option>
          </select>
        </label>
        <label className={row}>
          <span className={lbl}>Attachments</span>
          <input className={input} value={p.attachmentsPreference ?? ''} onChange={(e) => set('attachmentsPreference', e.target.value || null)} placeholder="e.g. minimal, conventional" />
        </label>
        <label className={row}>
          <span className={lbl}>Trim line</span>
          <input className={input} value={p.trimLine ?? ''} onChange={(e) => set('trimLine', e.target.value || null)} placeholder="e.g. scalloped / straight" />
        </label>
        <label className={row}>
          <span className={lbl}>Staging</span>
          <input className={input} value={p.staging ?? ''} onChange={(e) => set('staging', e.target.value || null)} placeholder="e.g. 0.25mm per step" />
        </label>
        <label className={row}>
          <span className={lbl}>Notes for the lab</span>
          <textarea className={input} rows={3} value={p.notes ?? ''} onChange={(e) => set('notes', e.target.value || null)} />
        </label>
      </div>
      <div className="flex justify-end">
        <button
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setOk(null);
            portalApi
              .savePreferences(p)
              .then(() => setOk('Preferences saved.'))
              .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false));
          }}
          className="rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-accent-fg disabled:opacity-40"
        >
          {busy ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------

export function PortalProfile() {
  const [p, setP] = useState<WfClinicProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    portalApi.profile().then(setP).catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);
  const set = <K extends keyof WfClinicProfile>(k: K, v: WfClinicProfile[K]) => setP((prev) => (prev ? { ...prev, [k]: v } : prev));
  if (err) return <Msg>{err}</Msg>;
  if (!p) return <p className="text-muted">Loading…</p>;
  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Title sub="How the lab reaches you and addresses your deliveries">Clinic profile</Title>
      {ok && <Msg ok>{ok}</Msg>}
      <div className={`${card} space-y-3`}>
        <div className={row}>
          <span className={lbl}>Group ID</span>
          <div className="pt-2 font-mono text-sm font-semibold text-ink">
            {p.code} <span className="ml-2 font-sans text-xs font-normal text-muted">Set by the lab - used on every case and invoice</span>
          </div>
        </div>
        <label className={row}>
          <span className={lbl}>Clinic name *</span>
          <input className={input} value={p.clinicName} onChange={(e) => set('clinicName', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Doctor name</span>
          <input className={input} value={p.doctorName ?? ''} onChange={(e) => set('doctorName', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Phone</span>
          <input className={input} value={p.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>WhatsApp</span>
          <input className={input} value={p.whatsapp ?? ''} onChange={(e) => set('whatsapp', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Email</span>
          <input className={input} value={p.email ?? ''} onChange={(e) => set('email', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>City</span>
          <input className={input} value={p.city ?? ''} onChange={(e) => set('city', e.target.value)} />
        </label>
        <label className={row}>
          <span className={lbl}>Delivery address</span>
          <textarea className={input} rows={3} value={p.address ?? ''} onChange={(e) => set('address', e.target.value)} />
        </label>
      </div>
      <div className="flex justify-end">
        <button
          disabled={busy || !p.clinicName.trim()}
          onClick={() => {
            setBusy(true);
            setOk(null);
            const { code: _code, ...rest } = p;
            void _code;
            portalApi
              .saveProfile(rest)
              .then(() => setOk('Profile saved.'))
              .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false));
          }}
          className="rounded-lg bg-accent px-6 py-2.5 text-sm font-semibold text-accent-fg disabled:opacity-40"
        >
          {busy ? 'Saving…' : 'Save profile'}
        </button>
      </div>
    </div>
  );
}
